import {
	type KeyboardEvent,
	type PointerEvent,
	useEffect,
	useLayoutEffect,
	useRef,
} from "react";

const SCROLL_KEYS = new Set([
	" ",
	"ArrowDown",
	"ArrowUp",
	"End",
	"Home",
	"PageDown",
	"PageUp",
]);

const IGNORE_WHEEL_AFTER_CONTROL_MS = 500;

type OverflowSnapshot = { element: HTMLElement; scrollTop: number };

const isOverflowScroller = (element: HTMLElement) => {
	const { overflowY } = getComputedStyle(element);
	return (
		overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay"
	);
};

const collectOverflowAncestors = (node: HTMLElement): OverflowSnapshot[] => {
	const snapshots: OverflowSnapshot[] = [];
	let current: HTMLElement | null = node.parentElement;

	while (current) {
		if (
			isOverflowScroller(current) &&
			current.scrollHeight > current.clientHeight + 1
		) {
			snapshots.push({ element: current, scrollTop: current.scrollTop });
		}
		current = current.parentElement;
	}

	return snapshots;
};

const applyOverflowSnapshots = (snapshots: OverflowSnapshot[]) => {
	for (const snapshot of snapshots) {
		if (snapshot.element.scrollTop === snapshot.scrollTop) continue;
		snapshot.element.scrollTop = snapshot.scrollTop;
	}
};

/**
 * Keeps the field list from jumping when a control is focused or the draft
 * re-renders. Wheel/trackpad inertia after a click must not overwrite the
 * saved offset, and non-user scroll (focus/layout) is put back immediately.
 */
export const usePreservedOverflowScroll = <T extends HTMLElement>() => {
	const ref = useRef<T>(null);
	const scrollTopRef = useRef(0);
	const ancestorsRef = useRef<OverflowSnapshot[]>([]);
	const restoringRef = useRef(false);
	const allowRecordRef = useRef(false);
	const ignoreWheelUntilRef = useRef(0);
	const wheelFrameRef = useRef<null | number>(null);

	const snapshotAncestors = () => {
		const node = ref.current;
		if (!node) return;
		ancestorsRef.current = collectOverflowAncestors(node);
	};

	const recordFieldScroll = () => {
		const node = ref.current;
		if (!node) return;
		scrollTopRef.current = node.scrollTop;
		snapshotAncestors();
	};

	const restore = () => {
		const node = ref.current;
		if (!node) return;

		restoringRef.current = true;
		if (node.scrollTop !== scrollTopRef.current) {
			node.scrollTop = scrollTopRef.current;
		}
		applyOverflowSnapshots(ancestorsRef.current);
		restoringRef.current = false;
	};

	const cancelPendingWheelRecord = () => {
		if (wheelFrameRef.current === null) return;
		cancelAnimationFrame(wheelFrameRef.current);
		wheelFrameRef.current = null;
	};

	useLayoutEffect(restore);

	useEffect(() => {
		const node = ref.current;
		if (!node) return;

		snapshotAncestors();
		scrollTopRef.current = node.scrollTop;

		const focusDescriptor = Object.getOwnPropertyDescriptor(
			HTMLElement.prototype,
			"focus",
		);
		const originalFocus = HTMLElement.prototype.focus;
		// Proxy avoids `this` (React Compiler skips functions that use it).
		const patchedFocus = new Proxy(originalFocus, {
			apply(target, thisArg, argArray) {
				const options = argArray[0] as FocusOptions | undefined;
				if (thisArg instanceof Node && node.contains(thisArg)) {
					return Reflect.apply(target, thisArg, [
						{ ...options, preventScroll: true },
					]);
				}
				return Reflect.apply(target, thisArg, argArray);
			},
		});

		Object.defineProperty(HTMLElement.prototype, "focus", {
			configurable: true,
			value: patchedFocus,
			writable: true,
		});

		const onFocusIn = () => {
			restore();
			requestAnimationFrame(restore);
		};

		node.addEventListener("focusin", onFocusIn, true);

		return () => {
			cancelPendingWheelRecord();
			if (focusDescriptor) {
				Object.defineProperty(HTMLElement.prototype, "focus", focusDescriptor);
			} else {
				HTMLElement.prototype.focus = originalFocus;
			}
			node.removeEventListener("focusin", onFocusIn, true);
		};
	}, []);

	return {
		onKeyDownCapture: (event: KeyboardEvent<T>) => {
			if (SCROLL_KEYS.has(event.key)) allowRecordRef.current = true;
		},
		onPointerDownCapture: (event: PointerEvent<T>) => {
			const onScrollerChrome = event.target === event.currentTarget;
			allowRecordRef.current = onScrollerChrome;
			if (onScrollerChrome) return;

			ignoreWheelUntilRef.current = Date.now() + IGNORE_WHEEL_AFTER_CONTROL_MS;
			cancelPendingWheelRecord();
			restore();
		},
		onScroll: () => {
			if (restoringRef.current) return;

			if (allowRecordRef.current) {
				recordFieldScroll();
				allowRecordRef.current = false;
				return;
			}

			restore();
			requestAnimationFrame(restore);
		},
		onTouchMoveCapture: () => {
			allowRecordRef.current = true;
		},
		onWheelCapture: () => {
			if (Date.now() < ignoreWheelUntilRef.current) return;

			allowRecordRef.current = true;
			cancelPendingWheelRecord();
			wheelFrameRef.current = requestAnimationFrame(() => {
				wheelFrameRef.current = null;
				if (Date.now() < ignoreWheelUntilRef.current) return;
				recordFieldScroll();
			});
		},
		ref,
	};
};
